import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { withTranslation } from "react-i18next";
import { translateText } from "../../../services/translate";
import { useState } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

function ComplianceDialog({
  t,
  open,
  compliance,
  errors,
  onClose,
  onSave,
  onChange,
}) {
  const isEdit = compliance?.id && compliance.id !== -1;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const [generatingField, setGeneratingField] = useState(null);

  const handleGenerateField = async (
    sourceValue,
    targetField,
    targetLanguage,
  ) => {
    setGeneratingField(targetField);

    try {
      const translated = await translateText(sourceValue, targetLanguage);
      onChange({ [targetField]: translated });
    } catch (err) {
      console.error(err);
      setSnackbarMessage(t("error_generating_translation"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setGeneratingField(null);
    }
  };

  function enabledIcon(field) {
    if (field === "name") {
      return (
        !!compliance?.name_es &&
        !compliance?.name_en &&
        generatingField === null
      );
    }
    return (
      !!compliance?.description_es &&
      !compliance?.description_en &&
      generatingField === null
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {isEdit ? t("edit_compliance") : t("add_compliance")}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: "column",
            minWidth: 400,
          }}
        >
          <TextField
            required
            id="insert_name_es"
            label={t("Name_es")}
            type="text"
            fullWidth
            variant="standard"
            error={errors?.name_es}
            value={compliance?.name_es || ""}
            onChange={(e) => onChange({ name_es: e.target.value })}
          />
          <TextField
            id="insert_description"
            label={t("Description_es")}
            type="text"
            fullWidth
            variant="standard"
            value={compliance?.description_es || ""}
            onChange={(e) => onChange({ description_es: e.target.value })}
          />
          <TextField
            required
            label={t("Name_en")}
            variant="standard"
            error={errors?.name_en}
            value={compliance?.name_en || ""}
            onChange={(e) => onChange({ name_en: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={enabledIcon("name") && t("Auto generate")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!enabledIcon("name")}
                        onClick={() =>
                          handleGenerateField(
                            compliance.name_es,
                            "name_en",
                            "en",
                          )
                        }
                      >
                        {generatingField === "name_en" ? (
                          <CircularProgress size={18} />
                        ) : (
                          <AutoFixHighIcon
                            color={enabledIcon("name") ? "primary" : "disabled"}
                            fontSize="small"
                          />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t("Description_en")}
            variant="standard"
            value={compliance?.description_en || ""}
            onChange={(e) => onChange({ description_en: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={enabledIcon("description") && t("Auto generate")}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!enabledIcon("description")}
                        onClick={() =>
                          handleGenerateField(
                            compliance.description_es,
                            "description_en",
                            "en",
                          )
                        }
                      >
                        {generatingField === "description_en" ? (
                          <CircularProgress size={18} />
                        ) : (
                          <AutoFixHighIcon
                            color={
                              enabledIcon("description")
                                ? "primary"
                                : "disabled"
                            }
                            fontSize="small"
                          />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={generatingField !== null}>
            {t("Cancel")}
          </Button>
          <Button onClick={onSave} disabled={generatingField !== null}>
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert elevation={6} variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withTranslation()(ComplianceDialog);
